import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Observable, Subject, takeUntil, tap } from 'rxjs';
import { RolloutProgressReportDefinitionsService } from '../../../service/definitions/rollout-progress-report-definitions.service';
import { RolloutProgressReportEntities } from '../../../service/entities';
import { RolloutProgressReportGroupsService } from '../../../service/groups/rollout-progress-report-groups.service';
import { RolloutProgressReportsService } from '../../../service/reports/rollout-progress-reports.service';
import { ReportGroupComponent } from './report-group/report-group.component';

@Component({
  selector: 'app-report-groups',
  imports: [CommonModule, ReportGroupComponent],
  templateUrl: './report-groups.component.html',
  styleUrl: './report-groups.component.scss',
})
export class ReportGroupsComponent implements OnInit, OnDestroy {
  @Input() typeId?: RolloutProgressReportEntities.v3.MarkerTypes;

  reportGroupsWithDefinitions: RolloutProgressReportEntities.v3.ReportGroupWithDefinitions[];

  reportId: string = this.reportService.getStoredReportId();
  storedReportGroups$ = this.reportGroupService.getStoredReportGroupByReportId$(
    this.reportId,
  );
  cancelSub$ = new Subject<void>();

  combinedDefinitionsAndGroups$: Observable<
    [
      RolloutProgressReportEntities.v3.DefinitionsWithGroupInfoResponse,
      RolloutProgressReportEntities.v3.ReportGroupsResponse,
    ]
  >;

  constructor(
    private reportService: RolloutProgressReportsService,
    private reportGroupService: RolloutProgressReportGroupsService,
    private definitionService: RolloutProgressReportDefinitionsService,
  ) {}

  ngOnInit(): void {
    this.combineReportGroupAndDefinitions();
  }

  ngOnDestroy(): void {
    this.cancelSub$.next();
    this.cancelSub$.complete();
  }

  combineReportGroupAndDefinitions = (): void => {
    if (this.typeId) {
      this.combinedDefinitionsAndGroups$ = combineLatest([
        this.definitionService.getStoredDefinitionsByPointType$(this.typeId),
        this.storedReportGroups$,
      ]).pipe(
        takeUntil(this.cancelSub$),
        tap(([storedDefinitions, storedReportGroups]) => {
          this.addDefinitionsToGroup(storedDefinitions, storedReportGroups);
        }),
      );
    }
  };

  addDefinitionsToGroup = (
    storedDefinitions: RolloutProgressReportEntities.v3.DefinitionsWithGroupInfoResponse,
    storedReportGroups: RolloutProgressReportEntities.v3.ReportGroupsResponse,
  ): void => {
    this.reportGroupsWithDefinitions = storedReportGroups.items.map(
      (storedReportGroup) =>
        ({
          ...storedReportGroup,
          definitions: this.findDefinitionsByGroupName(
            storedDefinitions,
            storedReportGroup,
          ),
        }) as unknown as RolloutProgressReportEntities.v3.ReportGroupWithDefinitions,
    );
  };

  findDefinitionsByGroupName = (
    storedDefinitions: RolloutProgressReportEntities.v3.DefinitionsWithGroupInfoResponse,
    storedReportGroup: RolloutProgressReportEntities.v3.AnswersByGroupItem,
  ): RolloutProgressReportEntities.v3.Definition[] | undefined =>
    storedDefinitions.items.find(
      (definitionSection) =>
        definitionSection.groupInfo.groupName ===
        storedReportGroup.group.groupInfo.groupName,
    )?.selfInspectionDefinitions;
}
